import React, { useEffect, useRef } from "react";
import { useTranslation } from "@helpers/translate";
import useOptionSearch from "./useOptionSearch";

/**
 * Inside of the dropdown: the search field, the selected option first,
 * a divider, the other options. Mounted on each opening, so the query starts empty.
 */
const SearchableSelectList = ({ options, value, onSelect, renderOption, maxHeight, autoFocus }) => {
  const { __ } = useTranslation();
  const searchInputRef = useRef(null);
  const { query, setQuery, selectedOption, otherOptions } = useOptionSearch(options, value);

  useEffect(() => {
    if (autoFocus) {
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const renderOptionButton = (option, isSelected) => (
    <button
      type="button"
      key={option.key}
      className={`searchable-select__option ${
        isSelected ? "searchable-select__option--selected" : ""
      }`}
      onClick={() => onSelect(option)}
      role="option"
      aria-selected={isSelected}
    >
      {renderOption(option)}
    </button>
  );

  const hasOptions = Boolean(selectedOption) || otherOptions.length > 0;

  return (
    <div className="searchable-select__dropdown" style={{ maxHeight: `${maxHeight}px` }}>
      <div className="searchable-select__search">
        <input
          ref={searchInputRef}
          type="text"
          className="searchable-select__search-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={__("common.search")}
          autoComplete="off"
        />
      </div>

      <div className="searchable-select__options" role="listbox">
        {selectedOption && renderOptionButton(selectedOption, true)}

        {selectedOption && otherOptions.length > 0 && (
          <div className="searchable-select__divider" role="separator" />
        )}

        {otherOptions.map((option) => renderOptionButton(option, false))}

        {!hasOptions && (
          <div className="searchable-select__empty">{__("profile.nothing_found")}</div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelectList;
